import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Leaf, Eye, EyeOff, ArrowLeft, User, Stethoscope, ShieldCheck, Mail, CheckCircle, Lock, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, isAuthenticated, isPractitioner, isAdmin } = useAuth();
  const { t, language } = useLanguage();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // User type toggle: 'client' or 'practitioner'
  const [userType, setUserType] = useState('client');
  
  // Practitioner signup steps: 'request', 'verify', 'create'
  const [practitionerStep, setPractitionerStep] = useState('request');
  const [codeVerified, setCodeVerified] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'client'
  });
  
  // Practitioner request form
  const [practitionerRequestForm, setPractitionerRequestForm] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  
  // Contract acceptance
  const [contractAccepted, setContractAccepted] = useState(false);
  const [showContract, setShowContract] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      if (isAdmin) {
        navigate('/admin');
      } else if (isPractitioner) {
        navigate('/practitioner/dashboard');
      } else {
        navigate('/client/dashboard');
      }
    }
  }, [isAuthenticated, isPractitioner, isAdmin, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginForm.email, loginForm.password);
      toast.success('Connexion réussie !');
      if (user.is_admin) {
        navigate('/admin');
      } else if (user.role === 'practitioner') {
        navigate('/practitioner/dashboard');
      } else {
        navigate('/client/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(registerForm);
      toast.success('Compte créé avec succès !');
      if (user.role === 'practitioner') {
        navigate('/practitioner/setup');
      } else {
        navigate('/client/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  // Handle practitioner code request
  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/practitioner-request`, practitionerRequestForm);
      toast.success(t('practitionerSignup.codeSent') || 'Code envoyé par email !');
      setVerifiedEmail(practitionerRequestForm.email);
      setPractitionerStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du code");
    } finally {
      setLoading(false);
    }
  };

  // Handle code verification
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-practitioner-code`, {
        email: verifiedEmail,
        code: accessCode
      });
      toast.success(t('practitionerSignup.codeVerified') || 'Code vérifié !');
      setCodeVerified(true);
      setPractitionerStep('create');
      // Pre-fill registration form
      setRegisterForm({
        ...registerForm,
        email: verifiedEmail,
        first_name: practitionerRequestForm.first_name,
        last_name: practitionerRequestForm.last_name,
        role: 'practitioner'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  // Handle practitioner registration
  const handlePractitionerRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = {
        ...registerForm,
        role: 'practitioner',
        access_code: accessCode
      };
      const user = await register(formData);
      toast.success('Compte praticien créé avec succès !');
      navigate('/practitioner/setup');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  // Reset practitioner flow
  const resetPractitionerFlow = () => {
    setPractitionerStep('request');
    setCodeVerified(false);
    setAccessCode('');
    setVerifiedEmail('');
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="auth-page">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="/images/auth-bg.jpg"
          alt="Wellness"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-sage-900/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-white">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
            <Leaf className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-4xl font-semibold text-center mb-4">
            {t('auth.welcome')}
          </h1>
          <p className="text-lg text-white/80 text-center max-w-md">
            {t('auth.welcomeSubtitle')}
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col">
        {/* Back Button */}
        <div className="p-6">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('auth.backToHome')}
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
              <div className="w-12 h-12 bg-sage-500 rounded-xl flex items-center justify-center">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading text-2xl font-semibold">Opora Vie</span>
            </div>

            <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); resetPractitionerFlow(); }} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 bg-sand-100 p-1 rounded-xl">
                <TabsTrigger 
                  value="login" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="tab-login"
                >
                  {t('auth.login')}
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="tab-register"
                >
                  {t('auth.register')}
                </TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="font-heading text-2xl font-semibold mb-2">{t('auth.welcomeBack')}</h2>
                    <p className="text-muted-foreground">{t('auth.connectToAccount')}</p>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">{t('auth.email')}</Label>
                      <Input 
                        id="login-email"
                        type="email"
                        placeholder="votre@email.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        required
                        className="h-12 rounded-xl border-sand-200"
                        data-testid="login-email"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="login-password">{t('auth.password')}</Label>
                      <div className="relative">
                        <Input 
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                          required
                          className="h-12 rounded-xl border-sand-200 pr-10"
                          data-testid="login-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-sage-500 hover:bg-sage-600 rounded-xl font-medium"
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? '...' : t('auth.loginButton')}
                    </Button>
                  </form>
                </div>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="font-heading text-2xl font-semibold mb-2">{t('auth.createAccount')}</h2>
                    <p className="text-muted-foreground">{t('auth.joinPlatform') || 'Rejoignez la plateforme en moins d\'une minute.'}</p>
                  </div>

                  {/* User Type Toggle */}
                  <div className="flex bg-sand-100 p-1 rounded-xl">
                    <button
                      type="button"
                      onClick={() => { setUserType('client'); resetPractitionerFlow(); }}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                        userType === 'client' 
                          ? 'bg-white shadow-sm text-foreground' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="toggle-client"
                    >
                      <User className="w-4 h-4" />
                      {t('auth.iAmClient') || 'Je suis client'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setUserType('practitioner')}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                        userType === 'practitioner' 
                          ? 'bg-white shadow-sm text-foreground' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="toggle-practitioner"
                    >
                      <Stethoscope className="w-4 h-4" />
                      {t('auth.iAmPractitioner') || 'Je suis praticien'}
                    </button>
                  </div>

                  {/* Client Registration Form */}
                  {userType === 'client' && (
                    <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="register-firstname">{t('auth.firstName')}</Label>
                          <Input 
                            id="register-firstname"
                            type="text"
                            placeholder="Jean"
                            value={registerForm.first_name}
                            onChange={(e) => setRegisterForm({ ...registerForm, first_name: e.target.value })}
                            required
                            className="h-12 rounded-xl border-sand-200"
                            data-testid="register-firstname"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="register-lastname">{t('auth.lastName')}</Label>
                          <Input 
                            id="register-lastname"
                            type="text"
                            placeholder="Dupont"
                            value={registerForm.last_name}
                            onChange={(e) => setRegisterForm({ ...registerForm, last_name: e.target.value })}
                            required
                            className="h-12 rounded-xl border-sand-200"
                            data-testid="register-lastname"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="register-email">{t('auth.email')}</Label>
                        <Input 
                          id="register-email"
                          type="email"
                          placeholder="votre@email.com"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                          required
                          className="h-12 rounded-xl border-sand-200"
                          data-testid="register-email"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="register-phone">{t('auth.phoneOptional')}</Label>
                        <Input 
                          id="register-phone"
                          type="tel"
                          placeholder="06 12 34 56 78"
                          value={registerForm.phone}
                          onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                          className="h-12 rounded-xl border-sand-200"
                          data-testid="register-phone"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="register-password">{t('auth.password')}</Label>
                        <div className="relative">
                          <Input 
                            id="register-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={registerForm.password}
                            onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                            required
                            minLength={6}
                            className="h-12 rounded-xl border-sand-200 pr-10"
                            data-testid="register-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full h-12 bg-sage-500 hover:bg-sage-600 rounded-xl font-medium"
                        disabled={loading}
                        data-testid="register-submit"
                      >
                        {loading ? '...' : t('auth.createMyAccount')}
                      </Button>
                    </form>
                  )}

                  {/* Practitioner Registration Flow */}
                  {userType === 'practitioner' && (
                    <>
                      {/* Step 1: Request Code */}
                      {practitionerStep === 'request' && (
                        <div className="space-y-4">
                          {/* Info Box */}
                          <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 flex gap-3">
                            <ShieldCheck className="w-5 h-5 text-sage-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-sage-700">
                              <p className="font-medium mb-1">{t('auth.identityVerification') || 'Vérification d\'identité'}</p>
                              <p className="text-sage-600">
                                {t('auth.verificationExplanation') || 'Saisissez votre nom et votre email professionnel. Nous vous enverrons un code à 8 caractères pour valider votre inscription (valide 7 jours).'}
                              </p>
                            </div>
                          </div>

                          <form onSubmit={handleRequestCode} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="pract-firstname">{t('auth.firstName')}</Label>
                                <Input 
                                  id="pract-firstname"
                                  type="text"
                                  placeholder="Jean"
                                  value={practitionerRequestForm.first_name}
                                  onChange={(e) => setPractitionerRequestForm({ ...practitionerRequestForm, first_name: e.target.value })}
                                  required
                                  className="h-12 rounded-xl border-sand-200"
                                  data-testid="pract-firstname"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="pract-lastname">{t('auth.lastName')}</Label>
                                <Input 
                                  id="pract-lastname"
                                  type="text"
                                  placeholder="Dupont"
                                  value={practitionerRequestForm.last_name}
                                  onChange={(e) => setPractitionerRequestForm({ ...practitionerRequestForm, last_name: e.target.value })}
                                  required
                                  className="h-12 rounded-xl border-sand-200"
                                  data-testid="pract-lastname"
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="pract-email">{t('auth.email')}</Label>
                              <Input 
                                id="pract-email"
                                type="email"
                                placeholder="votre@email.com"
                                value={practitionerRequestForm.email}
                                onChange={(e) => setPractitionerRequestForm({ ...practitionerRequestForm, email: e.target.value })}
                                required
                                className="h-12 rounded-xl border-sand-200"
                                data-testid="pract-email"
                              />
                            </div>

                            <Button 
                              type="submit" 
                              className="w-full h-12 bg-sage-500 hover:bg-sage-600 rounded-xl font-medium flex items-center justify-center gap-2"
                              disabled={loading}
                              data-testid="send-code-btn"
                            >
                              <Mail className="w-4 h-4" />
                              {loading ? '...' : (t('auth.sendMyCode') || 'Envoyer mon code')}
                            </Button>
                          </form>
                        </div>
                      )}

                      {/* Step 2: Verify Code */}
                      {practitionerStep === 'verify' && (
                        <div className="space-y-4">
                          <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 flex gap-3">
                            <Mail className="w-5 h-5 text-sage-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-sage-700">
                              <p className="font-medium mb-1">{t('auth.codeReceived') || 'Code envoyé !'}</p>
                              <p className="text-sage-600">
                                {t('auth.checkEmail') || `Un code a été envoyé à ${verifiedEmail}. Vérifiez également vos spams.`}
                              </p>
                            </div>
                          </div>

                          <form onSubmit={handleVerifyCode} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="access-code">{t('auth.accessCode') || 'Code d\'accès'}</Label>
                              <Input 
                                id="access-code"
                                type="text"
                                placeholder="XXXXXXXX"
                                value={accessCode}
                                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                                required
                                maxLength={8}
                                className="h-12 rounded-xl border-sand-200 text-center text-xl tracking-widest font-mono"
                                data-testid="access-code-input"
                              />
                            </div>

                            <Button 
                              type="submit" 
                              className="w-full h-12 bg-sage-500 hover:bg-sage-600 rounded-xl font-medium flex items-center justify-center gap-2"
                              disabled={loading || accessCode.length < 8}
                              data-testid="verify-code-btn"
                            >
                              <CheckCircle className="w-4 h-4" />
                              {loading ? '...' : (t('auth.verifyCode') || 'Vérifier le code')}
                            </Button>

                            <button
                              type="button"
                              onClick={() => setPractitionerStep('request')}
                              className="w-full text-sm text-muted-foreground hover:text-foreground"
                            >
                              {t('auth.resendCode') || '← Renvoyer un code'}
                            </button>
                          </form>
                        </div>
                      )}

                      {/* Step 3: Create Account */}
                      {practitionerStep === 'create' && (
                        <div className="space-y-4">
                          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-green-700">
                              <p className="font-medium">{t('auth.codeVerifiedSuccess') || 'Code vérifié avec succès !'}</p>
                              <p className="text-green-600">{t('auth.createYourPassword') || 'Créez maintenant votre mot de passe.'}</p>
                            </div>
                          </div>

                          <form onSubmit={handlePractitionerRegister} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>{t('auth.firstName')}</Label>
                                <Input 
                                  type="text"
                                  value={registerForm.first_name}
                                  disabled
                                  className="h-12 rounded-xl border-sand-200 bg-sand-50"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('auth.lastName')}</Label>
                                <Input 
                                  type="text"
                                  value={registerForm.last_name}
                                  disabled
                                  className="h-12 rounded-xl border-sand-200 bg-sand-50"
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label>{t('auth.email')}</Label>
                              <Input 
                                type="email"
                                value={registerForm.email}
                                disabled
                                className="h-12 rounded-xl border-sand-200 bg-sand-50"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="pract-phone">{t('auth.phoneOptional')}</Label>
                              <Input 
                                id="pract-phone"
                                type="tel"
                                placeholder="06 12 34 56 78"
                                value={registerForm.phone}
                                onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                                className="h-12 rounded-xl border-sand-200"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="pract-password">{t('auth.password')}</Label>
                              <div className="relative">
                                <Input 
                                  id="pract-password"
                                  type={showPassword ? 'text' : 'password'}
                                  placeholder="••••••••"
                                  value={registerForm.password}
                                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                                  required
                                  minLength={6}
                                  className="h-12 rounded-xl border-sand-200 pr-10"
                                  data-testid="pract-password"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                              </div>
                            </div>

                            {/* Contract Section */}
                            <div className="space-y-3 border border-sand-200 rounded-xl p-4 bg-sand-50">
                              <div className="flex items-start gap-2">
                                <button
                                  type="button"
                                  onClick={() => setShowContract(!showContract)}
                                  className="text-sage-600 hover:text-sage-700 text-sm font-medium underline flex items-center gap-1"
                                >
                                  {showContract ? '▼' : '▶'} {language === 'en' ? 'View Practitioner Contract' : 'Voir le Contrat Praticien'}
                                </button>
                              </div>
                              
                              {showContract && (
                                <div className="max-h-64 overflow-y-auto text-xs text-muted-foreground bg-white p-3 rounded-lg border border-sand-200">
                                  {language === 'en' ? (
                                    <>
                                      <h4 className="font-bold mb-2">PRACTITIONER CONTRACT - OPORA VIE PLATFORM</h4>
                                      <p className="mb-2"><strong>1. PURPOSE</strong><br/>
                                      This contract governs the use of the Opora Vie platform by wellness practitioners (therapists, naturopaths, sophrologists, psychologists, etc.).</p>
                                      <p className="mb-2"><strong>2. SUBSCRIPTION FEES</strong><br/>
                                      • Initial registration fee: €50 (one-time payment)<br/>
                                      • Monthly subscription: €50/month<br/>
                                      • Payments are processed via Stripe</p>
                                      <p className="mb-2"><strong>3. SERVICES PROVIDED</strong><br/>
                                      • Professional profile visible to clients<br/>
                                      • Online appointment booking system<br/>
                                      • Video consultation capabilities<br/>
                                      • Messaging with clients<br/>
                                      • Appointment management dashboard</p>
                                      <p className="mb-2"><strong>4. PRACTITIONER OBLIGATIONS</strong><br/>
                                      • Provide accurate professional information<br/>
                                      • Maintain valid certifications and diplomas<br/>
                                      • Respond to client inquiries in a timely manner<br/>
                                      • Respect client confidentiality<br/>
                                      • Comply with applicable healthcare regulations</p>
                                      <p className="mb-2"><strong>5. TERMINATION</strong><br/>
                                      Either party may terminate this agreement with 30 days written notice. Upon termination, access to the platform will be revoked.</p>
                                      <p className="mb-2"><strong>6. LIABILITY</strong><br/>
                                      Opora Vie serves as a platform connecting practitioners with clients. The platform is not responsible for the quality of services provided by practitioners.</p>
                                    </>
                                  ) : (
                                    <>
                                      <h4 className="font-bold mb-2">CONTRAT PRATICIEN - PLATEFORME OPORA VIE</h4>
                                      <p className="mb-2"><strong>1. OBJET</strong><br/>
                                      Ce contrat régit l'utilisation de la plateforme Opora Vie par les praticiens du bien-être (thérapeutes, naturopathes, sophrologues, psychologues, etc.).</p>
                                      <p className="mb-2"><strong>2. FRAIS D'ABONNEMENT</strong><br/>
                                      • Frais d'inscription initial : 50€ (paiement unique)<br/>
                                      • Abonnement mensuel : 50€/mois<br/>
                                      • Les paiements sont traités via Stripe</p>
                                      <p className="mb-2"><strong>3. SERVICES FOURNIS</strong><br/>
                                      • Profil professionnel visible par les clients<br/>
                                      • Système de prise de rendez-vous en ligne<br/>
                                      • Capacités de consultation vidéo<br/>
                                      • Messagerie avec les clients<br/>
                                      • Tableau de bord de gestion des rendez-vous</p>
                                      <p className="mb-2"><strong>4. OBLIGATIONS DU PRATICIEN</strong><br/>
                                      • Fournir des informations professionnelles exactes<br/>
                                      • Maintenir des certifications et diplômes valides<br/>
                                      • Répondre aux demandes des clients en temps opportun<br/>
                                      • Respecter la confidentialité des clients<br/>
                                      • Se conformer aux réglementations sanitaires applicables</p>
                                      <p className="mb-2"><strong>5. RÉSILIATION</strong><br/>
                                      Chaque partie peut résilier cet accord avec un préavis écrit de 30 jours. À la résiliation, l'accès à la plateforme sera révoqué.</p>
                                      <p className="mb-2"><strong>6. RESPONSABILITÉ</strong><br/>
                                      Opora Vie sert de plateforme connectant les praticiens aux clients. La plateforme n'est pas responsable de la qualité des services fournis par les praticiens.</p>
                                    </>
                                  )}
                                </div>
                              )}
                              
                              <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={contractAccepted}
                                  onChange={(e) => setContractAccepted(e.target.checked)}
                                  className="mt-1 w-4 h-4 rounded border-sand-300 text-sage-600 focus:ring-sage-500"
                                  data-testid="contract-checkbox"
                                />
                                <span className="text-sm text-foreground">
                                  {language === 'en' 
                                    ? 'I have read and accept the Practitioner Contract (€50 initial + €50/month)'
                                    : "J'ai lu et j'accepte le Contrat Praticien (50€ initial + 50€/mois)"
                                  }
                                </span>
                              </label>
                            </div>

                            <Button 
                              type="submit" 
                              className="w-full h-12 bg-sage-500 hover:bg-sage-600 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={loading || !contractAccepted}
                              data-testid="create-practitioner-account-btn"
                            >
                              {loading ? '...' : (t('auth.createPractitionerAccount') || 'Créer mon compte praticien')}
                            </Button>
                          </form>
                        </div>
                      )}
                    </>
                  )}

                  {/* Already have account link */}
                  <p className="text-center text-sm text-muted-foreground">
                    {t('auth.alreadyHaveAccount') || 'Déjà un compte ?'}{' '}
                    <button
                      type="button"
                      onClick={() => setActiveTab('login')}
                      className="text-sage-600 hover:underline font-medium"
                    >
                      {t('auth.loginLink') || 'Se connecter'}
                    </button>
                  </p>

                  {/* Security badges for practitioners */}
                  {userType === 'practitioner' && (
                    <div className="mt-6 pt-6 border-t border-sand-200">
                      <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Lock className="w-3.5 h-3.5 text-sage-600" />
                          <span>{t('auth.gdprCompliant') || 'GDPR Compliant'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <CreditCard className="w-3.5 h-3.5 text-sage-600" />
                          <span>{t('auth.securePayments') || 'Secure payments via Stripe'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 text-sage-600" />
                          <span>{t('auth.dataProtection') || 'Data protection'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
